import { cn } from '@/lib/cn';
import { type JSX, createEffect } from 'solid-js';

export interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Visible label content; falls back to aria-label for icon-only use. */
  label?: JSX.Element | string;
  'aria-label'?: string;
  /** Tri-state "some selected" look; only meaningful while unchecked. */
  indeterminate?: boolean;
  disabled?: boolean;
  class?: string;
}

/** life-memo-style checkbox: brand-filled rounded box with a white check. */
export const Checkbox = (props: CheckboxProps) => (
  <label
    class={cn(
      // relative: containing block for the sr-only (absolute) input — without a
      // positioned ancestor, label-click focus scroll-jumps the page (UCH-445)
      'relative inline-flex cursor-pointer items-center gap-2 text-sm',
      props.disabled && 'cursor-not-allowed opacity-50',
      props.class,
    )}
  >
    <input
      type="checkbox"
      ref={(el) => {
        createEffect(() => {
          el.indeterminate = (props.indeterminate ?? false) && !props.checked;
        });
      }}
      checked={props.checked}
      onChange={(e) => props.onChange(e.currentTarget.checked)}
      disabled={props.disabled}
      aria-label={props['aria-label']}
      class="peer sr-only"
    />
    <span
      aria-hidden
      class={cn(
        'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
        'border-neutral-300 bg-white dark:border-neutral-600 dark:bg-neutral-900',
        'peer-checked:border-brand-600 peer-checked:bg-brand-600',
        props.indeterminate && !props.checked && 'border-brand-600 bg-brand-600',
        'peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500 peer-focus-visible:ring-offset-1',
      )}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="3.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
        class="h-2.5 w-2.5 text-white"
      >
        {/* pathLength=1 normalizes the stroke; dashoffset 1→0 draws the mark like a pen */}
        <path
          d={props.indeterminate && !props.checked ? 'M5 12h14' : 'M4 12.5 9.5 18 20 6.5'}
          pathLength="1"
          class={cn(
            '[stroke-dasharray:1] transition-[stroke-dashoffset] duration-200 ease-out motion-reduce:transition-none',
            props.checked || props.indeterminate
              ? '[stroke-dashoffset:0]'
              : '[stroke-dashoffset:1]',
          )}
        />
      </svg>
    </span>
    {props.label}
  </label>
);
